// lifetime-sim-v0 reader (docs/contracts/lifetime-sim-v0.md): a sequence of surfaces the engine moves on the pack they
// were made for, through its morph layer (viewport-api-v0, "Surfaces that change shape"). The engine reads the geometry
// itself; the app reads the header for the frames' times and ages and the scalar maps the surface can be lit by, and
// metrics.json for the growth chart. The first run is ba-life's dHCP fetal white-matter atlas, weeks 21–36; the second its neonatal
// atlas, 37–44 weeks postmenstrual age, whose metrics rows are keyed by `age_pma_weeks` (v3; `week_pma` in v2) rather than
// `gestational_week`.

import type { SimFiles } from './files.ts';
import type { LifetimeHeader, LifetimeScalar, LifetimeSurface } from './types.ts';

/** Seconds in a week: the fetal sequence's time base is seconds since its first frame, a frame a week. */
export const WEEK_S = 604800;

/** One row of metrics.json: a hemisphere's surface at a source week. */
export interface LifetimeMetric {
  hemisphere: string;
  /** The row's source week in the run's own age quantity: the fetal atlas's `gestational_week`, the neonatal atlas's `age_pma_weeks` (v3) or `week_pma` (v2); set on read. */
  week: number;
  gestational_week?: number;
  week_pma?: number;
  age_pma_weeks?: number;
  area_mm2: number;
  signed_volume_mm3?: number;
  [k: string]: unknown;
}

/** The contract's Methods as named paragraphs, where the header writes them that way; null for a plain string or none. */
export function methodsParagraphs(m: unknown): Record<string, string> | null {
  if (!m || typeof m !== 'object' || Array.isArray(m)) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(m as Record<string, unknown>)) if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  return Object.keys(out).length ? out : null;
}

/** The contract's Methods are named paragraphs; the sheet wants one text, so they're joined in the header's order. */
export function methodsLine(m: unknown): string {
  if (typeof m === 'string') return m;
  const paras = methodsParagraphs(m);
  if (!paras) return '';
  return Object.values(paras)
    .map((v) => v.replace(/[\s.]*$/, '.'))
    .join(' ');
}

export class LifetimeSim {
  private metricsP: Promise<LifetimeMetric[]> | null = null;

  private constructor(
    readonly files: SimFiles,
    /** The header with Methods as one text (the contract's paragraphs joined), so it reads as the other formats' do. */
    readonly header: LifetimeHeader,
    /** The same Methods kept apart, by the contract's names (cohort_scans, geometry, interpretation…); null for a plain string. */
    readonly paragraphs: Record<string, string> | null,
    /** The frames' times, s since the first frame. */
    readonly times: Float64Array,
    /** The frames' ages, in the header's age unit (gestational weeks for the fetal atlas). */
    readonly ages: number[],
  ) {}

  static async open(files: SimFiles, raw: LifetimeHeader): Promise<LifetimeSim> {
    const where = `sims/${raw.id}`;
    const values = raw.time?.values;
    if (!Array.isArray(values) || values.length < 1 || !values.every((v) => typeof v === 'number' && Number.isFinite(v))) throw new Error(`${where}: a lifetime-sim-v0 run without time.values`);
    for (let i = 1; i < values.length; i++) if (!(values[i]! > values[i - 1]!)) throw new Error(`${where}: time.values must be strictly increasing`);
    if (!raw.surfaces?.length) throw new Error(`${where}: a lifetime-sim-v0 run without surfaces`);
    for (const s of raw.surfaces) if (typeof s.id !== 'string' || !s.positions || !s.indices) throw new Error(`${where}: surface ${String(s.id)} without positions or indices`);
    const ages = raw.age?.values?.length === values.length ? raw.age.values : values.map((t) => (raw.age?.at_time_zero ?? 0) + (t - values[0]!) / WEEK_S);
    const header: LifetimeHeader = { ...raw, methods: methodsLine(raw.methods) };
    return new LifetimeSim(files, header, methodsParagraphs(raw.methods), Float64Array.from(values), ages);
  }

  get surfaces(): LifetimeSurface[] {
    return this.header.surfaces ?? [];
  }

  /** The age unit's name for labels ('week'). */
  get ageUnit(): string {
    return this.header.age?.units ?? 'week';
  }

  /** The age at a time, by the frames around it (linear, clamped to the ends). */
  ageAt(t: number): number {
    const T = this.times;
    const n = T.length;
    if (n === 1 || t <= T[0]!) return this.ages[0]!;
    if (t >= T[n - 1]!) return this.ages[n - 1]!;
    let i = 1;
    while (i < n - 1 && T[i]! < t) i++;
    const w = (t - T[i - 1]!) / (T[i]! - T[i - 1]!);
    return this.ages[i - 1]! + w * (this.ages[i]! - this.ages[i - 1]!);
  }

  /** The scalar maps every surface publishes under the same name (the ones the whole surface can be lit by). */
  get maps(): LifetimeScalar[] {
    const first = this.surfaces[0]?.scalars ?? [];
    return first.filter((m) => this.surfaces.every((s) => s.scalars?.some((x) => x.name === m.name)));
  }

  /** Vertices and triangles over every surface. */
  get counts(): { surfaces: number; vertices: number; triangles: number } {
    return this.surfaces.reduce((acc, s) => ({ surfaces: acc.surfaces + 1, vertices: acc.vertices + (s.vertices ?? 0), triangles: acc.triangles + (s.triangles ?? 0) }), { surfaces: 0, vertices: 0, triangles: 0 });
  }

  /** metrics.json's rows (a hemisphere's area and volume at each source week); none when the run has no metrics. */
  metrics(): Promise<LifetimeMetric[]> {
    return (this.metricsP ??= this.files.maybeJson<unknown>('metrics.json').then((m) => {
      const rows = Array.isArray(m) ? m : m && typeof m === 'object' && Array.isArray((m as { metrics?: unknown }).metrics) ? (m as { metrics: unknown[] }).metrics : [];
      return rows.flatMap((r): LifetimeMetric[] => {
        if (!r || typeof r !== 'object' || typeof (r as { area_mm2?: unknown }).area_mm2 !== 'number') return [];
        const x = r as Record<string, unknown>;
        const week = [x.gestational_week, x.week_pma, x.age_pma_weeks, x.week].find((v) => typeof v === 'number' && Number.isFinite(v)) as number | undefined;
        return week === undefined ? [] : [{ ...x, week } as LifetimeMetric];
      });
    }));
  }

  /** The surfaces' summed area, mm², at each frame, in frame order: the rows at that frame's age; null where there's none. */
  async areaByFrame(): Promise<(number | null)[]> {
    const rows = await this.metrics();
    return this.ages.map((age) => {
      const at = rows.filter((r) => Math.abs(r.week - age) < 1e-6);
      return at.length ? at.reduce((a, r) => a + r.area_mm2, 0) : null;
    });
  }

  /** The summed area at a time, mm²: linear between the frames around it (as the engine draws the surface), clamped. */
  async areaAt(t: number): Promise<number | null> {
    const areas = await this.areaByFrame();
    const T = this.times;
    const n = T.length;
    if (n === 1 || t <= T[0]!) return areas[0] ?? null;
    if (t >= T[n - 1]!) return areas[n - 1] ?? null;
    let i = 1;
    while (i < n - 1 && T[i]! < t) i++;
    const a = areas[i - 1];
    const b = areas[i];
    if (a === null || a === undefined || b === null || b === undefined) return null;
    const w = (t - T[i - 1]!) / (T[i]! - T[i - 1]!);
    return a + w * (b - a);
  }
}
